"use client";

import { motion } from "framer-motion";
import { Wrench, Cog, KeyRound, ArrowLeft } from "lucide-react";
import Link from "next/link";

export default function BlogMaintenance() {
  return (
    <section className="relative min-h-[80vh] flex items-center justify-center overflow-hidden bg-gradient-to-br from-slate-50 via-blue-50/40 to-violet-50/40 px-6 py-20">
      {/* Tło: rozmyte plamy */}
      <div className="pointer-events-none absolute -top-32 -left-32 h-96 w-96 rounded-full bg-blue-300/30 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-32 -right-32 h-96 w-96 rounded-full bg-violet-300/30 blur-3xl" />

      <div className="relative max-w-2xl text-center">
        {/* Animowane zębatki i klucz */}
        <div className="relative mx-auto mb-10 h-40 w-40">
          {/* Duża zębatka */}
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
            className="absolute inset-0 flex items-center justify-center text-blue-500/80"
          >
            <Cog className="h-32 w-32 drop-shadow-[0_8px_24px_rgba(59,130,246,0.35)]" strokeWidth={1.5} />
          </motion.div>

          {/* Mała zębatka (w prawo-dół, kręci się w drugą stronę) */}
          <motion.div
            animate={{ rotate: -360 }}
            transition={{ duration: 5, repeat: Infinity, ease: "linear" }}
            className="absolute -bottom-2 -right-2 text-violet-500/80"
          >
            <Cog className="h-16 w-16 drop-shadow-[0_4px_16px_rgba(139,92,246,0.4)]" strokeWidth={1.5} />
          </motion.div>

          {/* Klucz — lekko buja */}
          <motion.div
            animate={{ rotate: [0, -12, 0, 12, 0] }}
            transition={{ duration: 3.5, repeat: Infinity, ease: "easeInOut" }}
            className="absolute -left-3 top-2 text-amber-500"
          >
            <Wrench className="h-14 w-14 drop-shadow-[0_4px_16px_rgba(245,158,11,0.45)]" strokeWidth={1.8} />
          </motion.div>

          {/* Mała "iskra" / kluczyk */}
          <motion.div
            animate={{ y: [0, -6, 0], rotate: [0, 10, 0] }}
            transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
            className="absolute right-2 top-0 text-emerald-500"
          >
            <KeyRound className="h-9 w-9 drop-shadow-[0_3px_10px_rgba(16,185,129,0.45)]" strokeWidth={1.8} />
          </motion.div>
        </div>

        {/* Tekst */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <span className="inline-flex items-center gap-2 rounded-full bg-amber-100 px-3 py-1 text-xs font-bold uppercase tracking-[0.18em] text-amber-700">
            <span className="relative inline-flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-amber-400 opacity-75" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-amber-500" />
            </span>
            Prace techniczne
          </span>

          <h1 className="mt-5 text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight">
            Pracujemy nad czymś wyjątkowym
          </h1>
          <p className="mt-4 text-base sm:text-lg text-slate-600 leading-relaxed max-w-xl mx-auto">
            Nasz blog jest tymczasowo wyłączony — szykujemy nową odsłonę z dopracowanymi artykułami,
            poradami maturalnymi i materiałami, które naprawdę pomogą Ci się przygotować. Wrócimy lada chwila!
          </p>
        </motion.div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.15 }}
          className="mt-8 flex flex-wrap items-center justify-center gap-3"
        >
          <Link
            href="/"
            className="inline-flex items-center gap-2 rounded-2xl bg-slate-900 px-5 py-3 text-sm font-semibold text-white shadow-lg shadow-slate-900/20 transition hover:-translate-y-0.5 hover:bg-slate-800"
          >
            <ArrowLeft className="h-4 w-4" />
            Wróć do strony głównej
          </Link>
          <Link
            href="/platforma/login"
            className="inline-flex items-center gap-2 rounded-2xl border-2 border-slate-200 bg-white px-5 py-3 text-sm font-semibold text-slate-700 transition hover:-translate-y-0.5 hover:border-blue-300 hover:text-blue-600"
          >
            Zaloguj się na platformę
          </Link>
        </motion.div>

        {/* Pasek postępu (dekoracyjny) */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="mt-10 mx-auto h-1.5 w-64 overflow-hidden rounded-full bg-slate-200"
        >
          <motion.div
            animate={{ x: ["-100%", "100%"] }}
            transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
            className="h-full w-1/2 rounded-full bg-gradient-to-r from-blue-500 via-violet-500 to-emerald-500"
          />
        </motion.div>
      </div>
    </section>
  );
}
